/**
 * Data Provider for React-Admin
 *
 * Custom data provider with automatic token refresh functionality.
 * Uses the httpClient service for preemptive token refresh.
 *
 * Key features:
 * - Preemptive token refresh before expiration
 * - Automatic retry on 401 errors
 * - FormData support for file uploads
 */

import { fetchUtils } from 'react-admin';
import simpleRestProvider from 'ra-data-simple-rest';
import config from '../config';
import { setStoreIds } from './storeIdsStore';
import { setOrderStats, setNeedsALook } from './OrdersHUD/orderAnalyticsStore';
import {
  getValidToken,
  refreshToken,
} from '../services/tokenRefreshService';
import Logger from '../utils/Logger';

const apiUrl = `${config.API_URL}/admin`;

// Type for fetchUtils.fetchJson response
type FetchJsonResponse = { status: number; headers: Headers; body: string; json: any };

/**
 * HTTP client — delegates ALL token management to tokenRefreshService.
 *
 * Previously this had its own refreshSubscribers queue that was disconnected
 * from the singleton queue in tokenRefreshService. That caused the "logged in
 * but can't see data" bug: one system had the new token, the other didn't.
 *
 * Now: getValidToken() handles preemptive refresh + waiting for in-flight
 * refreshes via the single tokenRefreshService queue. On 401, refreshToken()
 * also goes through the same singleton gate.
 */
const httpClient = async (url: string, options: any = {}): Promise<FetchJsonResponse> => {
  if (!options.headers) {
    options.headers = new Headers(
      options.method !== 'DELETE'
        ? { Accept: 'application/json' }
        : { 'Content-Type': 'text/plain' }
    );
  }

  // Single call handles: read token, preemptive refresh if expiring, wait if refresh in-flight
  const token = await getValidToken();

  if (token) {
    options.headers.set('Authorization', `Bearer ${token}`);
  }

  try {
    return await fetchUtils.fetchJson(url, options) as FetchJsonResponse;
  } catch (error: any) {
    // On 401, try one refresh + retry (goes through singleton gate)
    if (error.status === 401 && token) {
      Logger.debug('[DataProvider] Got 401, attempting token refresh...');
      const newToken = await refreshToken();
      if (newToken) {
        options.headers.set('Authorization', `Bearer ${newToken}`);
        return await fetchUtils.fetchJson(url, options) as FetchJsonResponse;
      }
    }
    throw error;
  }
};

const updateBanerItemPosition = (position: string, itemsId: string) => {
  const body = JSON.stringify({ position: position });
  return httpClient(`${apiUrl}/banner-items/${itemsId}/move`, {
    method: 'PATCH',
    body: body,
  }).then(({ json }) => ({
    data: json,
    total: json.length,
  }));
};

const dataProvider = simpleRestProvider(apiUrl, httpClient);
const customDataProvider = {
  ...dataProvider,
  getMany: (resource: string, params: any) => {
    const query = new URLSearchParams();
    params.ids.forEach((id: string) => query.append('id', id));
    return httpClient(`${apiUrl}/${resource}?${query.toString()}`, {
      method: 'GET',
    }).then(({ json }) => {
      const data = Array.isArray(json) ? json : json.data;
      return { data: Array.isArray(data) ? data : [] };
    });
  },
  getList: (resource: string, params: any): Promise<any> => {
    const { page, perPage } = params.pagination;
    const filters = params.filter || {};
    const query = new URLSearchParams();
    query.set('page', page.toString());
    query.set('limit', perPage.toString());

    // Pass sort params to the API
    if (params.sort?.field) {
      query.set('sortBy', params.sort.field);
      query.set('sortOrder', params.sort.order || 'DESC');
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach((item) => query.append(key, String(item)));
        return;
      }
      query.set(key, String(value));
    });

    return httpClient(`${apiUrl}/${resource}?${query.toString()}`, {
      method: 'GET',
    }).then(({ json }) => {
      if (resource === 'orders') {
        setStoreIds(json.storeIds || []);
        if (json.stats) setOrderStats(json.stats);
        setNeedsALook(json.needsALook || null);

        return {
          data: json.data,
          total: json.total,
          meta: { storeIds: json.storeIds || [] },
        };
      }
      return {
        data: json.data,
        total: json.total,
      };
    });
  },
  getOne: (resource: string, params: any): Promise<any> => {
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'GET',
    }).then(({ json }) => {
      return {
        data: json,
        total: json.length,
      };
    });
  },
  update: (resource: string, params: any) => {
    let formData = new FormData();
    let data = params.data;
    switch (resource) {
      case 'closing-hours':
        data = params.data;
        break;
      case 'store-settings':
        data = params.data;
        break;
      case 'orders':
        data = {
          status: params.data.status,
        };
        break;
      case 'banners':
        data = {
          name: params.data.name,
          type: params.data.type,
          isActive: params.data.isActive,
        };
        break;
      case 'banner-items':
        formData.append('bannerId', params.data.bannerId);
        if (params.data?.image)
          formData.append(
            'image',
            params.data.image?.rawFile || params.data.image
          );
        formData.append('isActive', params.data.isActive);
        formData.append('isDefault', params.data.isDefault);
        formData.append('name', params.data.name);
        formData.append('redirectUrl', params.data.redirectUrl);
        // formData.append('number', params.data.number );
        break;
      case 'categories':
        // NEW: Handle category image uploads
        if (params.data?.image?.rawFile) {
          // Image is being uploaded
          formData.append('name', params.data.name);
          formData.append('isActive', String(params.data.isActive ?? false));
          formData.append('respectsEarlyCutoff', String(params.data.respectsEarlyCutoff ?? false));
          formData.append('image', params.data.image.rawFile);
        } else {
          // No image upload, just text fields
          data = {
            name: params.data.name,
            isActive: params.data.isActive,
            respectsEarlyCutoff: params.data.respectsEarlyCutoff,
          };
        }
        break;
      case 'webhook-endpoints':
        data = {
          type: params.data.type,
          url: params.data.url,
          isActive: params.data.isActive,
          events: params.data.events,
        };
        break;
    }
    const ReqData =
      (params.data?.image?.rawFile && ['banner-items', 'categories'].includes(resource)) ||
        resource === 'banner-items'
        ? formData
        : JSON.stringify(data);

    if (resource.indexOf('banner') > 0)
      updateBanerItemPosition(params.data.position, params.data.id);

    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'PATCH',
      body: ReqData,
    }).then(({ json }) => ({
      data: json,
      total: json.length,
    }));
  },
  delete: (resource: string, params: any) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'DELETE',
    }).then(({ json }) => {
      return {
        data: json,
        total: json.length,
      };
    }),
  create: (resource: any, params: any) => {
    let formData = new FormData();
    if (params.data.image) {
      formData.append('bannerId', params.data.bannerId);
      formData.append('image', params.data.image.rawFile);
      params.data.isActive && formData.append('isActive', params.data.isActive);
      params.data.isDefault &&
        formData.append('isDefault', params.data.isDefault);
      formData.append('name', params.data.name);
      params.data.redirectUrl &&
        formData.append('redirectUrl', params.data.redirectUrl);
    }
    const data = params.data.image ? formData : JSON.stringify(params.data);
    return httpClient(`${apiUrl}/${resource}`, {
      method: 'POST',
      body: data,
    })
      .then(({ json }) => ({
        data: json,
        total: json.length,
      }))
      .catch((error) => {
        return Promise.reject(JSON.stringify(error.body.developerMessage));
      });
  },
};

export default customDataProvider;

// Export httpClient for use in other components
export { httpClient };
